import type { LanguageId } from "@/schemas/common"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { Option, pipe } from "effect"
import { Header } from "react-aria-components"
import { getLanguageById } from "@/collections/languages"
import { getPostList } from "@/collections/posts/list/client"
import { Container } from "@/components/layout/Container"
import { PostListTable } from "@/features/blog/components/PostListTable"
import { pageMeta } from "@/utils/seo"

export const Route = createFileRoute("/_blog/_archive/posts/$lang/")({
  component: PostArchiveComponent,
  loader: async ({ params }) => {
    const language = pipe(
      getLanguageById(params.lang as LanguageId),
      Option.getOrThrowWith(() => notFound()),
    )
    return {
      language,
      posts: await getPostList({
        filters: {
          language: language.id,
        },
      }),
    }
  },
  head: ({ loaderData, params }) => {
    const languageName = loaderData?.language.englishName ?? params.lang
    const localizedLanguageName = loaderData?.language.localName
      ? `${loaderData.language.englishName} (${loaderData.language.localName})`
      : languageName

    return {
      meta: pageMeta({
        ogType: "website",
        title: `Blog Archive [${languageName}]`,
        description: `View the archive of ${localizedLanguageName} blog posts on jingsi.space.`,
      }),
    }
  },
})

function PostArchiveComponent() {
  const { language, posts } = Route.useLoaderData()

  return (
    <Container>
      <Header>
        <h1>
          List of Posts in
          {" "}
          {
            language.localName
              ? (
                  <span title={language.englishName}>
                    {language.localName}
                  </span>
                )
              : (
                  <span>
                    {language.englishName}
                  </span>
                )
          }
        </h1>
      </Header>

      <main>
        <PostListTable posts={posts} />
      </main>
    </Container>
  )
}
